<template>
  <div class="content">
    <div class="pathway-dashboard-wrap">
      <card class="card no-border-card" no-body>
        <div class="glad-pathway-dashboard">

          <!-- Header ───────────────────────────────────────────── -->
          <header class="gp-head">
            <div class="gp-head__text">
              <h1 class="gp-title">{{ isLearnerView ? 'My Learning Pathways' : 'Pathways' }}</h1>
              <p class="gp-sub">
                {{ isLearnerView
                    ? 'Your personalized professional learning journey.'
                    : 'Build and publish pathways for RTCs and educators.' }}
              </p>
            </div>
            <div class="gp-head__cta">
              <button class="gp-btn gp-btn--ghost" @click="showPurchase = true">
                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="9" cy="21" r="1"></circle><circle cx="20" cy="21" r="1"></circle><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path></svg>
                Purchase pathways
              </button>
              <template v-if="canCreate">
                <button class="gp-btn gp-btn--ghost" @click="openTemplate">
                  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 17H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v4"></path><rect x="7" y="11" width="14" height="10" rx="2"></rect></svg>
                  Create from template
                </button>
                <button class="gp-btn gp-btn--primary" @click="$router.push('/glad/pathways/new')">
                  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>
                  Create pathway
                </button>
              </template>
            </div>
          </header>

          <!-- Stat cards ───────────────────────────────────────── -->
          <section v-if="!isLearnerView" class="gp-stats">
            <div class="gp-stat">
              <div class="gp-stat__icon gp-stat__icon--violet">
                <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline></svg>
              </div>
              <div class="gp-stat__body">
                <div class="gp-stat__value">{{ stats.total }}</div>
                <div class="gp-stat__label">Total pathways</div>
                <div class="gp-stat__hint">Across all statuses</div>
              </div>
            </div>
            <div class="gp-stat">
              <div class="gp-stat__icon gp-stat__icon--green">
                <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg>
              </div>
              <div class="gp-stat__body">
                <div class="gp-stat__value">{{ stats.published }}</div>
                <div class="gp-stat__label">Published</div>
                <div class="gp-stat__hint">Visible to learners</div>
              </div>
            </div>
            <div class="gp-stat">
              <div class="gp-stat__icon gp-stat__icon--amber">
                <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 20h9"></path><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4z"></path></svg>
              </div>
              <div class="gp-stat__body">
                <div class="gp-stat__value">{{ stats.drafts }}</div>
                <div class="gp-stat__label">Drafts</div>
                <div class="gp-stat__hint">Not yet published</div>
              </div>
            </div>
            <div class="gp-stat">
              <div class="gp-stat__icon gp-stat__icon--gray">
                <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="21 8 21 21 3 21 3 8"></polyline><rect x="1" y="3" width="22" height="5"></rect><line x1="10" y1="12" x2="14" y2="12"></line></svg>
              </div>
              <div class="gp-stat__body">
                <div class="gp-stat__value">{{ stats.archived }}</div>
                <div class="gp-stat__label">Archived</div>
                <div class="gp-stat__hint">Not visible</div>
              </div>
            </div>
          </section>

          <!-- Filters bar ──────────────────────────────────────── -->
          <section class="gp-filters">
            <div class="gp-search">
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="gp-search__icon"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>
              <input v-model="search" type="text" placeholder="Search pathways..." class="gp-search__input" />
            </div>
            <div class="gp-select">
              <select v-model="filterStatus">
                <option value="">All statuses</option>
                <option value="published">Published</option>
                <option value="draft">Draft</option>
                <option value="archived">Archived</option>
              </select>
              <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="gp-select__chev"><polyline points="6 9 12 15 18 9"></polyline></svg>
            </div>
            <div class="gp-select">
              <select v-model="filterCategory">
                <option value="">All categories</option>
                <option value="foundations">Foundations</option>
                <option value="en_espanol">En Español</option>
                <option value="preschool">Preschool</option>
                <option value="secondary">Secondary</option>
              </select>
              <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="gp-select__chev"><polyline points="6 9 12 15 18 9"></polyline></svg>
            </div>
            <div class="gp-select">
              <select v-model="sortKey">
                <option value="updated_desc">Sort: Updated (newest)</option>
                <option value="updated_asc">Sort: Updated (oldest)</option>
                <option value="title_asc">Sort: Title (A–Z)</option>
                <option value="title_desc">Sort: Title (Z–A)</option>
              </select>
              <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="gp-select__chev"><polyline points="6 9 12 15 18 9"></polyline></svg>
            </div>
            <div class="gp-view-toggle">
              <button :class="['gp-view-btn', { 'is-active': view === 'list' }]" @click="view = 'list'" aria-label="List view">
                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="8" y1="6" x2="21" y2="6"></line><line x1="8" y1="12" x2="21" y2="12"></line><line x1="8" y1="18" x2="21" y2="18"></line><line x1="3" y1="6" x2="3.01" y2="6"></line><line x1="3" y1="12" x2="3.01" y2="12"></line><line x1="3" y1="18" x2="3.01" y2="18"></line></svg>
              </button>
              <button :class="['gp-view-btn', { 'is-active': view === 'grid' }]" @click="view = 'grid'" aria-label="Grid view">
                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="7" height="7"></rect><rect x="14" y="3" width="7" height="7"></rect><rect x="14" y="14" width="7" height="7"></rect><rect x="3" y="14" width="7" height="7"></rect></svg>
              </button>
            </div>
          </section>

          <!-- Loading / empty ──────────────────────────────────── -->
          <div v-if="loading" class="gp-loading">
            <span class="gp-spinner" aria-label="Loading pathways"></span>
          </div>
          <div v-else-if="filtered.length === 0" class="gp-empty">
            <p>No pathways match your filters.</p>
          </div>

          <!-- Table view ───────────────────────────────────────── -->
          <div v-else-if="view === 'list'" class="gp-table-wrap">
            <table class="gp-table">
              <thead>
                <tr>
                  <th class="gp-th-title" @click="toggleSort('title')">
                    Title
                    <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="gp-th-sort"><polyline points="6 9 12 4 18 9"></polyline><polyline points="6 15 12 20 18 15"></polyline></svg>
                  </th>
                  <th>Description</th>
                  <th>Modules</th>
                  <th>Est. Time</th>
                  <th>Status</th>
                  <th @click="toggleSort('updated')">
                    Updated
                    <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="gp-th-sort"><polyline points="6 9 12 4 18 9"></polyline><polyline points="6 15 12 20 18 15"></polyline></svg>
                  </th>
                  <th class="gp-th-actions">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(p, idx) in paginated" :key="'p-' + p.id" @click="openPathway(p.id)">
                  <td class="gp-cell-title">
                    <div class="gp-title-cell">
                      <div :class="['gp-icon-tile', iconClass(p, idx)]" v-html="iconSvg(p, idx)"></div>
                      <span class="gp-row-title">{{ p.title }}</span>
                    </div>
                  </td>
                  <td class="gp-cell-desc">{{ rowDescription(p) }}</td>
                  <td class="gp-cell-modules">{{ moduleCount(p) }} modules</td>
                  <td class="gp-cell-time">{{ estDuration(p) }} min</td>
                  <td>
                    <span :class="['gp-pill', pillClass(p.status)]">{{ pillLabel(p.status) }}</span>
                  </td>
                  <td class="gp-cell-updated">{{ formatDate(p.updated_at) }}</td>
                  <td class="gp-cell-actions" @click.stop>
                    <button class="gp-icon-btn" @click="openPathway(p.id)" aria-label="Open pathway">
                      <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path><polyline points="15 3 21 3 21 9"></polyline><line x1="10" y1="14" x2="21" y2="3"></line></svg>
                    </button>
                    <button class="gp-icon-btn" @click="toggleMenu(p.id)" aria-label="More actions">
                      <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor"><circle cx="5" cy="12" r="1.6"/><circle cx="12" cy="12" r="1.6"/><circle cx="19" cy="12" r="1.6"/></svg>
                    </button>
                    <div v-if="openMenuId === p.id" class="gp-menu" @click.stop>
                      <button @click="editPathway(p.id)">Edit</button>
                      <button @click="duplicatePathway(p)">Duplicate</button>
                      <button class="gp-menu__danger" @click="archivePathway(p)">{{ p.status === 'archived' ? 'Unarchive' : 'Archive' }}</button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Grid view (alternate) ────────────────────────────── -->
          <div v-else class="gp-grid">
            <article v-for="(p, idx) in paginated" :key="'g-' + p.id" class="gp-card" @click="openPathway(p.id)" tabindex="0" role="button">
              <div :class="['gp-card__icon', iconClass(p, idx)]" v-html="iconSvg(p, idx)"></div>
              <div class="gp-card__body">
                <span :class="['gp-pill', pillClass(p.status)]">{{ pillLabel(p.status) }}</span>
                <h3 class="gp-card__title">{{ p.title }}</h3>
                <p class="gp-card__desc">{{ rowDescription(p) }}</p>
                <div class="gp-card__meta">
                  <span>{{ moduleCount(p) }} modules</span>
                  <span class="gp-card__sep">·</span>
                  <span>{{ estDuration(p) }} min</span>
                </div>
                <div class="gp-card__foot">
                  <span>Updated {{ formatDate(p.updated_at) }}</span>
                </div>
              </div>
            </article>
          </div>

          <!-- Pagination ───────────────────────────────────────── -->
          <footer v-if="filtered.length > 0" class="gp-paging">
            <span class="gp-paging__info">
              Showing {{ rangeStart }} to {{ rangeEnd }} of {{ filtered.length }} pathways
            </span>
            <div class="gp-paging__pager">
              <button class="gp-icon-btn" :disabled="page === 1" @click="page = Math.max(1, page - 1)" aria-label="Previous page">
                <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"></polyline></svg>
              </button>
              <button v-for="n in totalPages" :key="n" :class="['gp-page-btn', { 'is-active': n === page }]" @click="page = n">{{ n }}</button>
              <button class="gp-icon-btn" :disabled="page === totalPages" @click="page = Math.min(totalPages, page + 1)" aria-label="Next page">
                <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"></polyline></svg>
              </button>
            </div>
          </footer>

        </div>
      </card>

      <!-- ───── Template gallery modal ──────────────────────────── -->
      <transition name="gp-modal-fade">
        <div
          v-if="templateGalleryOpen"
          class="gp-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="gp-tpl-title"
          @click.self="closeTemplateGallery">
          <div class="gp-modal__panel" @keydown.esc.stop="closeTemplateGallery">
            <header class="gp-modal__head">
              <div>
                <h2 id="gp-tpl-title" class="gp-modal__title">Choose a template</h2>
                <p class="gp-modal__sub">Start from a pre-built pathway. You can customize everything afterwards.</p>
              </div>
              <button class="gp-modal__close" type="button" aria-label="Close" @click="closeTemplateGallery">
                <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
              </button>
            </header>

            <div class="gp-tpl-grid">
              <button
                v-for="tpl in templates"
                :key="tpl.id"
                type="button"
                class="gp-tpl-card"
                @click="pickTemplate(tpl)">
                <div :class="['gp-tpl-card__icon', 'gp-tpl-card__icon--' + tpl.accent]">
                  <svg v-if="tpl.icon === 'plus'"     viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>
                  <svg v-else-if="tpl.icon === 'book'"     viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"></path><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"></path></svg>
                  <svg v-else-if="tpl.icon === 'award'"    viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="8" r="7"></circle><polyline points="8.21 13.89 7 23 12 20 17 23 15.79 13.88"></polyline></svg>
                  <svg v-else-if="tpl.icon === 'users'"    viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><path d="M23 21v-2a4 4 0 0 0-3-3.87"></path><path d="M16 3.13a4 4 0 0 1 0 7.75"></path></svg>
                  <svg v-else-if="tpl.icon === 'sparkles'" viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3l2 4 4 2-4 2-2 4-2-4-4-2 4-2z"></path><path d="M19 14l1 2 2 1-2 1-1 2-1-2-2-1 2-1z"></path></svg>
                  <svg v-else-if="tpl.icon === 'compass'"  viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76"></polygon></svg>
                </div>
                <div class="gp-tpl-card__body">
                  <div class="gp-tpl-card__title">{{ tpl.name }}</div>
                  <div class="gp-tpl-card__desc">{{ tpl.description }}</div>
                  <div class="gp-tpl-card__meta">
                    <span v-if="tpl.track" class="gp-tpl-card__chip">{{ trackLabel(tpl.track) }}</span>
                    <span class="gp-tpl-card__hint">{{ tpl.moduleHint }}</span>
                    <span class="gp-tpl-card__dot" aria-hidden="true">·</span>
                    <span class="gp-tpl-card__hint">{{ tpl.duration }}</span>
                  </div>
                </div>
                <div class="gp-tpl-card__arrow" aria-hidden="true">
                  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="5" y1="12" x2="19" y2="12"></line><polyline points="12 5 19 12 12 19"></polyline></svg>
                </div>
              </button>
            </div>
          </div>
        </div>
      </transition>

      <!-- ───── Pathway purchase (cart → Authorize.Net) ─────────── -->
      <pathway-purchase-modal
        :show.sync="showPurchase"
        :pathways="pathways"
        @completed="onPurchaseCompleted" />
    </div>
  </div>
</template>

<script>
import { GLAD_PATHWAY_TEMPLATES } from '@/utils/gladPathwayTemplates';
import PathwayPurchaseModal from '@/components/glad/PathwayPurchaseModal.vue';

const ICON_PRESETS = [
  { cls: 'gp-icon-tile--violet', svg: '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"></path></svg>' },
  { cls: 'gp-icon-tile--green',  svg: '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 10v6M2 10l10-5 10 5-10 5z"></path><path d="M6 12v5c3 3 9 3 12 0v-5"></path></svg>' },
  { cls: 'gp-icon-tile--amber',  svg: '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 16 14"></polyline></svg>' },
  { cls: 'gp-icon-tile--pink',   svg: '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><path d="M23 21v-2a4 4 0 0 0-3-3.87"></path><path d="M16 3.13a4 4 0 0 1 0 7.75"></path></svg>' },
  { cls: 'gp-icon-tile--indigo', svg: '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="20" x2="18" y2="10"></line><line x1="12" y1="20" x2="12" y2="4"></line><line x1="6" y1="20" x2="6" y2="14"></line></svg>' },
  { cls: 'gp-icon-tile--gray',   svg: '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="21 8 21 21 3 21 3 8"></polyline><rect x="1" y="3" width="22" height="5"></rect><line x1="10" y1="12" x2="14" y2="12"></line></svg>' },
];

export default {
  name: 'PathwayDashboard',
  components: { PathwayPurchaseModal },
  data() {
    return {
      loading: true,
      pathways: [],
      showPurchase: false,
      search: '',
      filterStatus: '',
      filterCategory: '',
      sortKey: 'updated_desc',
      view: 'list',
      page: 1,
      perPage: 10,
      openMenuId: null,
      gladRole: (typeof localStorage !== 'undefined') ? localStorage.getItem('hot-user') : '',
      templateGalleryOpen: false,
      templates: GLAD_PATHWAY_TEMPLATES,
    };
  },
  computed: {
    isNtcAdmin() { return this.gladRole === 'glad_ntc_admin'; },
    isRtcAdmin() { return this.gladRole === 'glad_rtc_admin'; },
    isLearnerView() { return !this.isNtcAdmin && !this.isRtcAdmin; },
    canCreate() { return this.isNtcAdmin; },
    stats() {
      const total = this.pathways.length;
      let published = 0, drafts = 0, archived = 0;
      this.pathways.forEach(p => {
        if (p.status === 'published') published += 1;
        else if (p.status === 'archived') archived += 1;
        else drafts += 1;
      });
      return { total, published, drafts, archived };
    },
    filtered() {
      const q = this.search.trim().toLowerCase();
      let list = this.pathways.filter(p => {
        if (q && !(p.title || '').toLowerCase().includes(q) && !(p.subtitle || '').toLowerCase().includes(q)) return false;
        if (this.filterStatus && p.status !== this.filterStatus) return false;
        if (this.filterCategory && p.track !== this.filterCategory) return false;
        return true;
      });
      list = list.slice().sort((a, b) => {
        switch (this.sortKey) {
          case 'updated_asc':  return new Date(a.updated_at || 0) - new Date(b.updated_at || 0);
          case 'title_asc':    return (a.title || '').localeCompare(b.title || '');
          case 'title_desc':   return (b.title || '').localeCompare(a.title || '');
          case 'updated_desc':
          default:             return new Date(b.updated_at || 0) - new Date(a.updated_at || 0);
        }
      });
      return list;
    },
    totalPages() { return Math.max(1, Math.ceil(this.filtered.length / this.perPage)); },
    paginated() {
      const start = (this.page - 1) * this.perPage;
      return this.filtered.slice(start, start + this.perPage);
    },
    rangeStart() { return this.filtered.length === 0 ? 0 : (this.page - 1) * this.perPage + 1; },
    rangeEnd() { return Math.min(this.filtered.length, this.page * this.perPage); },
  },
  watch: {
    filtered() { if (this.page > this.totalPages) this.page = 1; },
  },
  created() {
    this.fetchPathways();
    document.addEventListener('click', this.closeMenus);
  },
  beforeDestroy() {
    document.removeEventListener('click', this.closeMenus);
  },
  methods: {
    fetchPathways() {
      this.loading = true;
      this.$http.get('glad/pathways')
        .then(resp => { this.pathways = Array.isArray(resp.data) ? resp.data : []; })
        .catch(() => { this.pathways = []; })
        .finally(() => { this.loading = false; });
    },
    openPathway(id) {
      if (this.isNtcAdmin) { this.$router.push('/glad/pathways/' + id + '/edit'); return; }
      this.$router.push('/glad/pathways/' + id);
    },
    onPurchaseCompleted(payload) {
      // Mock purchase succeeded — grant access by enrolling the buyer in each
      // purchased pathway, then refresh the list so they show as unlocked.
      const ids = (payload && payload.pathwayIds) || [];
      if (!ids.length) { this.fetchPathways(); return; }
      Promise.all(
        ids.map(id => this.$http.post('glad/pathways/' + id + '/enroll').catch(() => null))
      ).then(() => {
        if (this.$toast && this.$toast.success) {
          this.$toast.success('Purchase complete — your pathways are unlocked.', { timeout: 4000 });
        }
        this.fetchPathways();
      });
    },
    editPathway(id) { this.$router.push('/glad/pathways/' + id + '/edit'); this.openMenuId = null; },
    openTemplate() {
      this.templateGalleryOpen = true;
      this.$nextTick(() => {
        document.addEventListener('keydown', this.handleTemplateKeydown);
      });
    },
    closeTemplateGallery() {
      this.templateGalleryOpen = false;
      document.removeEventListener('keydown', this.handleTemplateKeydown);
    },
    handleTemplateKeydown(e) {
      if (e.key === 'Escape') this.closeTemplateGallery();
    },
    pickTemplate(tpl) {
      this.closeTemplateGallery();
      if (!tpl || tpl.id === 'blank') {
        this.$router.push('/glad/pathways/new');
        return;
      }
      this.$router.push({ path: '/glad/pathways/new', query: { template: tpl.id } });
    },
    trackLabel(track) {
      const map = { foundations: 'Foundations', strategies: 'Strategies', leadership: 'Leadership', coaching: 'Coaching' };
      return map[track] || track;
    },
    duplicatePathway() { this.openMenuId = null; },
    archivePathway() { this.openMenuId = null; },
    toggleMenu(id) { this.openMenuId = this.openMenuId === id ? null : id; },
    closeMenus() { this.openMenuId = null; },
    toggleSort(field) {
      if (field === 'title') this.sortKey = this.sortKey === 'title_asc' ? 'title_desc' : 'title_asc';
      if (field === 'updated') this.sortKey = this.sortKey === 'updated_desc' ? 'updated_asc' : 'updated_desc';
    },
    moduleCount(p) {
      if (typeof p.module_count === 'number') return p.module_count;
      return Array.isArray(p.modules) ? p.modules.length : 0;
    },
    estDuration(p) { return Math.max(5, this.moduleCount(p) * 12); },
    rowDescription(p) {
      const text = p.subtitle || p.description || '';
      if (text.length <= 80) return text;
      return text.slice(0, 78).trimEnd() + '…';
    },
    formatDate(s) {
      if (!s) return '—';
      const d = new Date(s);
      if (Number.isNaN(d.getTime())) return '—';
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    },
    pillLabel(status) {
      if (status === 'published') return 'Published';
      if (status === 'archived')  return 'Archived';
      return 'Draft';
    },
    pillClass(status) {
      return {
        'gp-pill--green': status === 'published',
        'gp-pill--amber': !status || status === 'draft',
        'gp-pill--gray':  status === 'archived',
      };
    },
    iconClass(p, idx) {
      if (p.status === 'archived') return ICON_PRESETS[5].cls;
      const seed = (p.id || idx) % 5;
      return ICON_PRESETS[seed].cls;
    },
    iconSvg(p, idx) {
      const preset = p.status === 'archived' ? ICON_PRESETS[5] : ICON_PRESETS[(p.id || idx) % 5];
      return preset.svg;
    },
  },
};
</script>

<style scoped lang="scss">
@import '~@/assets/sass/glad-tokens.scss';

.pathway-dashboard-wrap { width: 100%; }
.pathway-dashboard-wrap ::v-deep > .card.no-border-card { border: 0; box-shadow: none; background: transparent; }
.glad-pathway-dashboard {
  padding: 1.75rem 2rem 2.5rem;
  font-family: 'Inter', 'Helvetica Neue', Arial, sans-serif;
  color: #1b1e25;
  background: #f8f8f5;
  min-height: 100%;
}

/* Header ──────────────────────────────────────────────────── */
.gp-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1.25rem;
  margin-bottom: 1.75rem;
  flex-wrap: wrap;
}
.gp-title {
  font-family: 'Fraunces', 'Source Serif Pro', Georgia, serif;
  font-size: 2rem;
  font-weight: 700;
  letter-spacing: -0.015em;
  color: #0f1115;
  margin: 0 0 0.35rem;
  line-height: 1.1;
}
.gp-sub {
  color: #5b6068;
  font-size: 0.95rem;
  margin: 0;
}
.gp-head__cta { display: flex; gap: 0.6rem; }

.gp-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.65rem 1.05rem;
  border-radius: 10px;
  font-family: var(--glad-font-sans);
  font-size: 0.88rem;
  font-weight: 600;
  cursor: pointer;
  border: 1px solid #d2cfc6;
  background: #fff;
  color: #0f1115;
  line-height: 1;
  transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease;
  box-shadow: 0 1px 1px rgba(15, 17, 21, 0.04);

  &:hover { border-color: #0f1115; }

  &.gp-btn--ghost { /* outlined */ }
  &.gp-btn--primary {
    background: #0f1115 !important;
    color: #ffffff !important;
    border-color: #0f1115 !important;
    &:hover { background: #1b1e25 !important; border-color: #1b1e25 !important; }
  }
}

/* Stat cards ──────────────────────────────────────────────── */
.gp-stats {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;

  @media (max-width: 960px) { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  @media (max-width: 520px) { grid-template-columns: 1fr; }
}
.gp-stat {
  background: #fff;
  border: 1px solid var(--glad-line);
  border-radius: 14px;
  padding: 1.1rem 1.2rem;
  display: flex;
  gap: 0.95rem;
  align-items: center;
}
.gp-stat__icon {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  &--violet { background: #ede9fe; color: #6d28d9; }
  &--green  { background: #dcfce7; color: #15803d; }
  &--amber  { background: #fef3c7; color: #b45309; }
  &--gray   { background: #f1f1ee; color: #5b6068; }
}
.gp-stat__value {
  font-family: 'Inter', 'Helvetica Neue', Arial, sans-serif;
  font-size: 1.6rem;
  font-weight: 700;
  line-height: 1.1;
  color: #0f1115;
}
.gp-stat__label {
  font-size: 0.85rem;
  font-weight: 600;
  color: #0f1115;
  margin-top: 0.15rem;
}
.gp-stat__hint {
  font-size: 0.75rem;
  color: #8a8f98;
  margin-top: 0.1rem;
}

/* Filters bar ─────────────────────────────────────────────── */
.gp-filters {
  display: flex;
  gap: 0.6rem;
  margin-bottom: 0.9rem;
  flex-wrap: wrap;
  align-items: center;
}
.gp-search {
  flex: 1;
  min-width: 220px;
  position: relative;
}
.gp-search__icon {
  position: absolute;
  left: 0.85rem;
  top: 50%;
  transform: translateY(-50%);
  color: var(--glad-text-muted);
  pointer-events: none;
}
.gp-search__input {
  width: 100%;
  padding: 0.6rem 0.85rem 0.6rem 2.25rem;
  border: 1px solid #e6e4dd;
  border-radius: 10px;
  background: #fff;
  font-size: 0.88rem;
  font-family: 'Inter', 'Helvetica Neue', Arial, sans-serif;
  color: #0f1115;
  outline: none;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
  &:focus { border-color: #0f1115; box-shadow: 0 0 0 3px rgba(15,17,21,0.06); }
}
.gp-select {
  position: relative;
  select {
    appearance: none;
    -webkit-appearance: none;
    padding: 0.6rem 2rem 0.6rem 0.85rem;
    border: 1px solid #e6e4dd;
    border-radius: 10px;
    background: #fff;
    font-size: 0.88rem;
    font-family: 'Inter', 'Helvetica Neue', Arial, sans-serif;
    color: #0f1115;
    cursor: pointer;
    min-width: 160px;
    outline: none;
    &:focus { border-color: #0f1115; }
  }
}
.gp-select__chev {
  position: absolute;
  right: 0.7rem;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
  color: var(--glad-text-muted);
}
.gp-view-toggle {
  display: inline-flex;
  border: 1px solid var(--glad-line);
  border-radius: 10px;
  background: #fff;
  margin-left: auto;
  overflow: hidden;
}
.gp-view-btn {
  width: 38px;
  height: 38px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 0;
  background: transparent;
  color: var(--glad-text-muted);
  cursor: pointer;
  &:hover { color: var(--glad-ink); }
  &.is-active { background: var(--glad-paper-2); color: var(--glad-ink); }
  & + & { border-left: 1px solid var(--glad-line); }
}

/* Loading / empty ─────────────────────────────────────────── */
.gp-loading { display: flex; justify-content: center; padding: 3rem 0; }
.gp-spinner {
  width: 32px; height: 32px;
  border: 3px solid var(--glad-line);
  border-top-color: var(--glad-ink);
  border-radius: 50%;
  animation: gp-spin 0.7s linear infinite;
}
@keyframes gp-spin { to { transform: rotate(360deg); } }
.gp-empty { text-align: center; padding: 3rem 0; color: var(--glad-text-soft); }

/* Table ───────────────────────────────────────────────────── */
.gp-table-wrap {
  background: #fff;
  border: 1px solid var(--glad-line);
  border-radius: 14px;
  overflow: hidden;
}
.gp-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.88rem;

  th, td {
    text-align: left;
    padding: 0.85rem 1rem;
    vertical-align: middle;
  }
  thead th {
    background: #fbfaf6;
    color: var(--glad-text-soft);
    font-weight: 600;
    font-size: 0.72rem;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    border-bottom: 1px solid var(--glad-line);
    cursor: default;
    user-select: none;

    &.gp-th-title { cursor: pointer; }
  }
  .gp-th-sort {
    color: var(--glad-text-muted);
    margin-left: 0.25rem;
    vertical-align: middle;
  }
  tbody tr {
    border-bottom: 1px solid var(--glad-line);
    cursor: pointer;
    transition: background 0.12s ease;
    &:hover { background: #fbfaf6; }
    &:last-child { border-bottom: 0; }
  }
  .gp-cell-desc {
    color: #5b6068;
    max-width: 280px;
    line-height: 1.45;
  }
  .gp-cell-modules,
  .gp-cell-time,
  .gp-cell-updated { color: #5b6068; white-space: nowrap; }
  .gp-th-actions, .gp-cell-actions { width: 110px; }
}
.gp-title-cell {
  display: flex;
  align-items: center;
  gap: 0.85rem;
}
.gp-row-title {
  font-weight: 600;
  color: #0f1115;
}

/* Icon tiles ──────────────────────────────────────────────── */
.gp-icon-tile {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  &--violet { background: #ede9fe; color: #6d28d9; }
  &--green  { background: #d1fae5; color: #047857; }
  &--amber  { background: #fef3c7; color: #b45309; }
  &--pink   { background: #fce7f3; color: #be185d; }
  &--indigo { background: #e0e7ff; color: #4338ca; }
  &--gray   { background: #f1f1ee; color: #5b6068; }
}

/* Pills ───────────────────────────────────────────────────── */
.gp-pill {
  display: inline-flex;
  align-items: center;
  padding: 0.2rem 0.65rem;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 600;

  &--green { background: #dcfce7; color: #15803d; }
  &--amber { background: #fef3c7; color: #92400e; }
  &--gray  { background: #f1f1ee; color: #5b6068; }
}

/* Action buttons ─────────────────────────────────────────── */
.gp-cell-actions {
  position: relative;
  white-space: nowrap;
}
.gp-icon-btn {
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #e6e4dd;
  border-radius: 8px;
  background: #fff;
  color: #5b6068;
  cursor: pointer;
  transition: border-color 0.15s ease, color 0.15s ease, background 0.15s ease;
  & + & { margin-left: 0.35rem; }
  &:hover { border-color: #0f1115; color: #0f1115; }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
}
.gp-menu {
  position: absolute;
  right: 0.5rem;
  top: 100%;
  margin-top: 4px;
  background: #fff;
  border: 1px solid var(--glad-line);
  border-radius: 10px;
  box-shadow: 0 8px 24px rgba(15,17,21,0.08);
  min-width: 140px;
  padding: 0.35rem;
  z-index: 20;
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
  button {
    text-align: left;
    padding: 0.5rem 0.6rem;
    background: transparent;
    border: 0;
    border-radius: 6px;
    font-size: 0.83rem;
    color: var(--glad-ink);
    cursor: pointer;
    &:hover { background: var(--glad-paper-2); }
    &.gp-menu__danger { color: var(--glad-danger); }
  }
}

/* Grid view ───────────────────────────────────────────────── */
.gp-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1rem;
}
.gp-card {
  background: #fff;
  border: 1px solid var(--glad-line);
  border-radius: 14px;
  padding: 1.1rem 1.2rem 1.15rem;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
  transition: border-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease;
  &:hover { border-color: var(--glad-ink); box-shadow: 0 8px 24px rgba(15,17,21,0.06); transform: translateY(-1px); }
  &:focus { outline: none; border-color: var(--glad-ink); }
}
.gp-card__icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  &.gp-icon-tile--violet { background: #ede9fe; color: #6d28d9; }
  &.gp-icon-tile--green  { background: #d1fae5; color: #047857; }
  &.gp-icon-tile--amber  { background: #fef3c7; color: #b45309; }
  &.gp-icon-tile--pink   { background: #fce7f3; color: #be185d; }
  &.gp-icon-tile--indigo { background: #e0e7ff; color: #4338ca; }
  &.gp-icon-tile--gray   { background: #f1f1ee; color: #5b6068; }
}
.gp-card__title {
  font-family: var(--glad-font-serif);
  font-size: 1.05rem;
  font-weight: 700;
  color: var(--glad-ink);
  margin: 0.5rem 0 0.3rem;
  line-height: 1.3;
}
.gp-card__desc {
  font-size: 0.85rem;
  color: var(--glad-text-soft);
  margin: 0 0 0.65rem;
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.gp-card__meta {
  font-size: 0.78rem;
  color: var(--glad-text-soft);
  display: flex;
  align-items: center;
  gap: 0.4rem;
}
.gp-card__sep { color: var(--glad-text-muted); }
.gp-card__foot {
  font-size: 0.72rem;
  color: var(--glad-text-muted);
  margin-top: auto;
}

/* Pagination ─────────────────────────────────────────────── */
.gp-paging {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 1.1rem;
  font-size: 0.85rem;
  color: var(--glad-text-soft);
}
.gp-paging__pager { display: inline-flex; gap: 0.35rem; align-items: center; }
.gp-page-btn {
  min-width: 32px;
  height: 32px;
  padding: 0 0.6rem;
  border: 1px solid #e6e4dd;
  border-radius: 8px;
  background: #fff;
  color: #0f1115;
  font-size: 0.85rem;
  cursor: pointer;
  &:hover { border-color: #0f1115; }
  &.is-active { background: #0f1115; color: #fff; border-color: #0f1115; }
}

/* Template gallery modal ──────────────────────────────────── */
.gp-modal {
  position: fixed;
  inset: 0;
  background: rgba(15, 17, 21, 0.45);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 5vh 1rem 2rem;
  z-index: 1080;
  overflow-y: auto;
  backdrop-filter: saturate(140%) blur(2px);
}
.gp-modal__panel {
  background: #ffffff;
  border-radius: 16px;
  width: 100%;
  max-width: 760px;
  box-shadow: 0 24px 60px rgba(15, 17, 21, 0.25);
  overflow: hidden;
  font-family: 'Inter', 'Helvetica Neue', Arial, sans-serif;
}
.gp-modal__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  padding: 1.25rem 1.5rem 1rem;
  border-bottom: 1px solid #ececec;
}
.gp-modal__title {
  font-family: 'Fraunces', 'Source Serif Pro', Georgia, serif;
  font-size: 1.35rem;
  font-weight: 700;
  color: #0f1115;
  margin: 0;
  letter-spacing: -0.01em;
  line-height: 1.2;
}
.gp-modal__sub {
  margin: 0.25rem 0 0;
  color: #5b6068;
  font-size: 0.9rem;
}
.gp-modal__close {
  width: 34px;
  height: 34px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: #5b6068;
  cursor: pointer;
  flex-shrink: 0;
  transition: background 0.12s ease, color 0.12s ease;
  &:hover { background: #f3f2ee; color: #0f1115; }
}

.gp-tpl-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem;
  padding: 1.25rem 1.5rem 1.5rem;
  @media (max-width: 640px) { grid-template-columns: 1fr; }
}
.gp-tpl-card {
  display: grid;
  grid-template-columns: 44px 1fr 20px;
  align-items: flex-start;
  gap: 0.85rem;
  padding: 1rem;
  text-align: left;
  background: #ffffff;
  border: 1px solid #e6e4dd;
  border-radius: 12px;
  cursor: pointer;
  transition: border-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease, background 0.15s ease;
  font-family: inherit;
  &:hover {
    border-color: #0f1115;
    background: #fafaf7;
    box-shadow: 0 4px 12px rgba(15, 17, 21, 0.06);
    transform: translateY(-1px);
    .gp-tpl-card__arrow { transform: translateX(2px); color: #0f1115; }
  }
  &:focus-visible {
    outline: none;
    border-color: #0f1115;
    box-shadow: 0 0 0 3px rgba(15, 17, 21, 0.12);
  }
}
.gp-tpl-card__icon {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  &--violet { background: #ede9fe; color: #6d28d9; }
  &--green  { background: #dcfce7; color: #15803d; }
  &--amber  { background: #fef3c7; color: #b45309; }
  &--pink   { background: #fce7f3; color: #be185d; }
  &--indigo { background: #e0e7ff; color: #4338ca; }
  &--gray   { background: #f1f1ee; color: #5b6068; }
}
.gp-tpl-card__body { min-width: 0; }
.gp-tpl-card__title {
  font-size: 0.95rem;
  font-weight: 600;
  color: #0f1115;
  line-height: 1.3;
  letter-spacing: -0.005em;
}
.gp-tpl-card__desc {
  margin-top: 0.25rem;
  font-size: 0.82rem;
  color: #5b6068;
  line-height: 1.45;
}
.gp-tpl-card__meta {
  display: flex;
  align-items: center;
  gap: 0.45rem;
  flex-wrap: wrap;
  margin-top: 0.6rem;
  font-size: 0.72rem;
  color: #8a8f98;
}
.gp-tpl-card__chip {
  background: #f3f2ee;
  color: #5b6068;
  font-weight: 500;
  padding: 0.1rem 0.55rem;
  border-radius: 999px;
  font-size: 0.7rem;
}
.gp-tpl-card__hint { color: #5b6068; }
.gp-tpl-card__dot  { color: #c4c4c0; }
.gp-tpl-card__arrow {
  align-self: center;
  color: #8a8f98;
  transition: transform 0.15s ease, color 0.15s ease;
}

/* fade in/out for the modal */
.gp-modal-fade-enter-active,
.gp-modal-fade-leave-active { transition: opacity 0.18s ease; }
.gp-modal-fade-enter,
.gp-modal-fade-leave-to    { opacity: 0; }
.gp-modal-fade-enter-active .gp-modal__panel,
.gp-modal-fade-leave-active .gp-modal__panel {
  transition: transform 0.18s ease, opacity 0.18s ease;
}
.gp-modal-fade-enter .gp-modal__panel,
.gp-modal-fade-leave-to .gp-modal__panel {
  transform: translateY(-8px);
  opacity: 0;
}
</style>
